// AuthModal — Parcours de connexion AYA (reproduction fidèle des maquettes 2026-10).
// Écran 1 : Connexion (CIV/Diaspora + téléphone) · Écran 2 : Vérification OTP · Écran 3 : Profil.
// LOGIQUE INCHANGÉE : endpoints /api/auth/request-otp, /verify-otp, /me/profile, session useAuth.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, X, Clock3, MessageCircle, Pencil, Headphones, ChevronRight, Check } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import FlagCi from "@/components/FlagCi";

const API = process.env.REACT_APP_BACKEND_URL || "";

const COMMUNES_CIV = [
  "Cocody", "Plateau", "Yopougon", "Marcory", "Treichville",
  "Adjamé", "Abobo", "Koumassi", "Port-Bouët", "Riviera", "Bingerville",
];

const DIASPORA_COUNTRIES = [
  { iso: "FR",  flag: "🇫🇷", dial: "+33",  label: "France" },
  { iso: "BE",  flag: "🇧🇪", dial: "+32",  label: "Belgique" },
  { iso: "CH",  flag: "🇨🇭", dial: "+41",  label: "Suisse" },
  { iso: "US",  flag: "🇺🇸", dial: "+1",   label: "USA" },
  { iso: "CA",  flag: "🇨🇦", dial: "+1",   label: "Canada" },
  { iso: "GB",  flag: "🇬🇧", dial: "+44",  label: "Royaume-Uni" },
  { iso: "IT",  flag: "🇮🇹", dial: "+39",  label: "Italie" },
  { iso: "DE",  flag: "🇩🇪", dial: "+49",  label: "Allemagne" },
  { iso: "ES",  flag: "🇪🇸", dial: "+34",  label: "Espagne" },
  { iso: "ML",  flag: "🇲🇱", dial: "+223", label: "Mali" },
  { iso: "OTHER", flag: "🌍", dial: "+",   label: "Autre pays" },
];

function fmtPhoneDisplay(dial, digits) {
  const grouped = (digits || "").replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  return `${dial} ${grouped}`;
}

// ── En-tête commun : masque AYA cerclé d'or + "🇨🇮 Bienvenue sur AYA" ──
function AuthHead({ onBack, onClose }) {
  return (
    <div className="aya-auth-head">
      {onBack && (
        <button type="button" className="aya-auth-circle-btn aya-auth-back" onClick={onBack} aria-label="Retour" data-testid="auth-back-btn">
          <ArrowLeft size={18} />
        </button>
      )}
      {onClose && (
        <button type="button" className="aya-auth-circle-btn aya-auth-close" onClick={onClose} aria-label="Fermer" data-testid="auth-modal-close">
          <X size={18} />
        </button>
      )}
      <span className="aya-auth-mask-ring">
        <img src="/aya/splash.png" alt="Masque AYA" className="aya-auth-mask" draggable={false} />
      </span>
      <div className="aya-auth-welcome" data-testid="auth-hero">
        <FlagCi size={22} style={{ margin: 0, borderRadius: 3 }} />
        <span>Bienvenue sur AYA</span>
      </div>
    </div>
  );
}

// ── Indicateur de progression 3 étapes : ✓ Numéro ── ② Vérification ── ③ Profil ──
function Progress({ current }) {
  const steps = [
    { n: 1, label: "Numéro" },
    { n: 2, label: "Vérification" },
    { n: 3, label: "Profil" },
  ];
  return (
    <div className="aya-auth-progress" data-testid="auth-progress">
      {steps.map((s, i) => {
        const state = s.n < current ? "done" : s.n === current ? "active" : "todo";
        return (
          <div key={s.n} className="aya-auth-prog-seg">
            {i > 0 && <span className={`aya-auth-prog-line ${s.n <= current ? "is-on" : ""}`} />}
            <span className="aya-auth-prog-step">
              <span className={`aya-auth-prog-dot is-${state}`}>
                {state === "done" ? <Check size={16} strokeWidth={3.2} /> : s.n}
              </span>
              <span className={`aya-auth-prog-label is-${state}`}>{s.label}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function AuthModal({ open, onClose }) {
  const { setSession } = useAuth();
  const [step, setStep] = useState(1);
  const [localDigits, setLocalDigits] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [displayName, setDisplayName] = useState("");
  const [commune, setCommune] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [pendingAuth, setPendingAuth] = useState(null);
  const [userMode, setUserMode] = useState("civ"); // "civ" | "diaspora"
  const [diasporaIso, setDiasporaIso] = useState("FR");
  const [otherDial, setOtherDial] = useState("");
  const [wantsCivAddress, setWantsCivAddress] = useState(true);
  // OTP = 6 chiffres (v5.30, conforme maquette). Bypass test : OWNER8 / MERCH4.
  const cellCount = 6;
  const codeInputs = useRef([]);

  const currentDial = userMode === "civ"
    ? "+225"
    : (diasporaIso === "OTHER"
        ? (otherDial.startsWith("+") ? otherDial : (otherDial ? `+${otherDial}` : "+"))
        : (DIASPORA_COUNTRIES.find((c) => c.iso === diasporaIso)?.dial || "+"));

  const fullPhone = userMode === "civ"
    ? (localDigits.length === 10 ? `+225${localDigits}` : null)
    : (localDigits.length >= 6 && currentDial !== "+" ? `${currentDial}${localDigits}` : null);

  const handlePhoneChange = (e) => {
    let v = e.target.value || "";
    if (userMode === "civ") {
      v = v.replace(/^\+?225/, "").replace(/\D/g, "").slice(0, 10);
    } else {
      v = v.replace(/\D/g, "").slice(0, 15);
    }
    setLocalDigits(v);
  };

  useEffect(() => {
    if (open) {
      setStep(1);
      setCode(["", "", "", "", "", ""]);
      setDevCode(null);
      setResendIn(0);
    }
  }, [open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((v) => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const requestOtp = useCallback(async () => {
    if (userMode === "civ" && localDigits.length !== 10) {
      toast.error("Numéro CIV à 10 chiffres requis");
      return;
    }
    if (userMode === "diaspora" && (localDigits.length < 6 || currentDial === "+")) {
      toast.error("Numéro diaspora invalide");
      return;
    }
    const normalized = fullPhone;
    if (!normalized) { toast.error("Numéro invalide"); return; }
    setBusy(true);
    try {
      const body = { phone: normalized };
      if (userMode === "diaspora") {
        body.user_type = "diaspora";
        body.residence_country = diasporaIso;
      } else {
        body.user_type = "civ";
      }
      const r = await fetch(`${API}/api/auth/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        toast.error(data.message || data.hint || data.error || `Erreur ${r.status}`);
        return;
      }
      if (data.dev_code) setDevCode(data.dev_code);
      setStep(2);
      setResendIn(60);
      toast.success("Code envoyé par SMS");
      setTimeout(() => codeInputs.current[0]?.focus(), 50);
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  }, [userMode, localDigits, fullPhone, currentDial, diasporaIso]);

  const verifyOtp = useCallback(async () => {
    const normalized = fullPhone;
    const fullCode = code.join("").toUpperCase();
    if (!normalized || fullCode.length < 6) {
      toast.error("Saisis le code complet");
      return;
    }
    setBusy(true);
    try {
      const body = { phone: normalized, code: fullCode };
      if (displayName.trim()) body.display_name = displayName.trim();
      if (commune) body.commune = commune;
      if (userMode === "diaspora") {
        body.user_type = "diaspora";
        body.residence_country = diasporaIso;
      } else {
        body.user_type = "civ";
      }
      const r = await fetch(`${API}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        toast.error(data.message || data.error || `Erreur ${r.status}`);
        return;
      }
      if (!data.user?.display_name) {
        setPendingAuth({ user: data.user, token: data.token, has_address: !!data.has_address, is_new: !!data.is_new });
        setStep(3);
        return;
      }
      setSession(data.user, data.token, { has_address: !!data.has_address, is_new: !!data.is_new });
      if (userMode === "diaspora" || data.user?.user_type === "diaspora") {
        toast.success(`Akwaba ${data.user.display_name} 🌍 Ton Espace Diaspora est prêt — bouton doré 🌍 en haut du chat.`, { duration: 6000 });
      } else {
        toast.success(data.is_new ? `Bienvenue ${data.user.display_name} !` : "Reconnecté");
      }
      onClose && onClose();
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  }, [fullPhone, code, displayName, commune, userMode, diasporaIso, setSession, onClose]);

  const saveProfile = useCallback(async () => {
    const name = displayName.trim();
    if (!name) { toast.error("Ton prénom est requis"); return; }
    setBusy(true);
    try {
      const token = pendingAuth?.token || localStorage.getItem("civ_user_token");
      const r = await fetch(`${API}/api/auth/me/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ display_name: name, commune: commune || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) {
        toast.error(d.error || `Erreur ${r.status}`);
        return;
      }
      if (pendingAuth?.user) {
        setSession(
          { ...pendingAuth.user, display_name: name, commune: commune || pendingAuth.user.commune || null },
          pendingAuth.token,
          { has_address: !!pendingAuth.has_address, is_new: !!pendingAuth.is_new }
        );
      }
      toast.success(`Bienvenue ${name.split(/\s+/)[0]} !`);
      setPendingAuth(null);
      onClose && onClose();
    } catch (_) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  }, [displayName, commune, pendingAuth, setSession, onClose]);

  const handleCodeChange = (idx, v) => {
    const ch = (v || "").replace(/[^A-Za-z0-9]/g, "").slice(-1).toUpperCase();
    setCode((arr) => {
      const next = arr.slice();
      next[idx] = ch;
      return next;
    });
    if (ch && idx < cellCount - 1) {
      codeInputs.current[idx + 1]?.focus();
    }
  };

  const handleCodeKey = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeInputs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter" && code.filter((c) => c).length >= 6) {
      verifyOtp();
    }
  };

  const handleCodePaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 6).toUpperCase();
    if (txt.length === 6) {
      e.preventDefault();
      setCode(txt.split(""));
      setTimeout(() => codeInputs.current[5]?.focus(), 30);
    }
  };

  const autofillDevCode = () => {
    if (!devCode) return;
    const clean = String(devCode).replace(/[^A-Za-z0-9]/g, "").slice(0, 6).toUpperCase();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < clean.length; i++) arr[i] = clean[i];
    setCode(arr);
    setTimeout(() => codeInputs.current[Math.min(clean.length - 1, 5)]?.focus(), 50);
  };

  const openSupport = () => {
    window.location.href = "/aya-prive?ctx=" + encodeURIComponent("Support AYA : j'ai besoin d'aide pour me connecter.");
  };

  const codeEntered = code.filter((c) => c).length;

  if (!open) return null;

  return (
    <div className="aya-auth-overlay" data-testid="auth-modal-overlay" onClick={onClose}>
      <div className="aya-auth-card" onClick={(e) => e.stopPropagation()} data-testid="auth-modal">

        {/* ═══════════ ÉCRAN 1 — CONNEXION / INSCRIPTION ═══════════ */}
        {step === 1 && (
          <>
            <AuthHead onClose={onClose} />
            <h2 className="aya-auth-title">Connexion à <span className="aya-auth-green">AYA</span></h2>
            <p className="aya-auth-sub">Ton numéro suffit. Aucun mot de passe.</p>

            <div className="aya-auth-mode" role="tablist" aria-label="Choisis ton type de compte">
              <button
                type="button"
                role="tab"
                aria-selected={userMode === "civ"}
                className={`aya-auth-mode-tab ${userMode === "civ" ? "is-active" : ""}`}
                onClick={() => { setUserMode("civ"); setLocalDigits(""); }}
                data-testid="auth-mode-civ"
              >
                <FlagCi size={17} style={{ margin: 0, borderRadius: 2 }} /> Côte d'Ivoire
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={userMode === "diaspora"}
                className={`aya-auth-mode-tab ${userMode === "diaspora" ? "is-active" : ""}`}
                onClick={() => { setUserMode("diaspora"); setLocalDigits(""); }}
                data-testid="auth-mode-diaspora"
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>🌍</span> Diaspora
              </button>
            </div>

            <label className="aya-auth-label">Numéro de téléphone</label>
            <div className="aya-auth-phone">
              {userMode === "civ" ? (
                <span className="aya-auth-dial">+225</span>
              ) : (
                <select
                  className="aya-auth-dial aya-auth-dial-select"
                  value={diasporaIso}
                  onChange={(e) => setDiasporaIso(e.target.value)}
                  data-testid="auth-diaspora-country-select"
                  aria-label="Pays de résidence"
                >
                  {DIASPORA_COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>{c.flag} {c.dial}</option>
                  ))}
                </select>
              )}
              <span className="aya-auth-phone-sep" />
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={userMode === "civ" ? 10 : 15}
                placeholder={userMode === "civ" ? "07 08 90 12 34" : "612 34 56 78"}
                value={localDigits}
                onChange={handlePhoneChange}
                onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                data-testid="auth-phone-input"
                autoFocus
              />
            </div>

            {userMode === "diaspora" && diasporaIso === "OTHER" && (
              <input
                type="tel"
                inputMode="tel"
                maxLength={5}
                placeholder="Préfixe pays (ex: +221)"
                value={otherDial}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^\d+]/g, "").slice(0, 5);
                  if (v && !v.startsWith("+")) v = "+" + v.replace(/\+/g, "");
                  setOtherDial(v);
                }}
                data-testid="auth-other-dial-input"
                className="aya-auth-other-dial"
              />
            )}
            {userMode === "diaspora" && (
              <label className="aya-auth-checkbox" data-testid="auth-diaspora-civ-address">
                <input
                  type="checkbox"
                  checked={wantsCivAddress}
                  onChange={(e) => setWantsCivAddress(e.target.checked)}
                />
                <span>📍 Je vis hors CI mais je veux mon adresse AYA en Côte d'Ivoire.</span>
              </label>
            )}

            <button
              type="button"
              className="aya-auth-primary"
              disabled={busy || (userMode === "civ" ? localDigits.length !== 10 : localDigits.length < 6)}
              onClick={requestOtp}
              data-testid="auth-request-otp-btn"
            >
              <span>{busy ? "Envoi…" : "Recevoir le code"}</span>
              <ArrowRight size={20} strokeWidth={2.6} className="aya-auth-primary-arrow" />
            </button>

            <p className="aya-auth-legal">
              En continuant, tu acceptes les{" "}
              <a href="/cgu" target="_blank" rel="noreferrer" className="aya-auth-legal-link" data-testid="auth-cgu-link">CGU</a>
              {" "}et la{" "}
              <a href="/confidentialite" target="_blank" rel="noreferrer" className="aya-auth-legal-link" data-testid="auth-privacy-link">politique de confidentialité</a>.
            </p>

            <div className="aya-auth-sep" data-testid="auth-pro-signup-block">
              <span className="aya-auth-sep-line" />
              <span className="aya-auth-sep-text">Vous êtes un professionnel ?</span>
              <span className="aya-auth-sep-line" />
            </div>
            <a href="/inscription-pro" className="aya-auth-gold-btn" data-testid="auth-pro-signup-link">
              <span>Créer un compte pro</span>
              <ArrowRight size={17} strokeWidth={2.4} />
            </a>
          </>
        )}

        {/* ═══════════ ÉCRAN 2 — VÉRIFICATION OTP ═══════════ */}
        {step === 2 && (
          <>
            <AuthHead onBack={() => setStep(1)} />
            <Progress current={2} />
            <h2 className="aya-auth-title" style={{ marginTop: 4 }}>Vérifie ton <span className="aya-auth-green">code</span></h2>
            <p className="aya-auth-sub" style={{ marginBottom: 4 }}>
              Nous avons envoyé un code SMS au
              <br />
              <strong className="aya-auth-sub-phone">{fmtPhoneDisplay(currentDial, localDigits)}</strong>
            </p>

            <div className="aya-auth-sms-halo" aria-hidden>
              <span className="aya-auth-sms-tick t1" /><span className="aya-auth-sms-tick t2" />
              <span className="aya-auth-sms-circle">
                <MessageCircle size={34} color="#0A2418" fill="#2EE27B" strokeWidth={1.4} />
              </span>
              <span className="aya-auth-sms-tick t3" /><span className="aya-auth-sms-tick t4" />
            </div>

            <div className="aya-auth-otp-row" onPaste={handleCodePaste}>
              {code.slice(0, cellCount).map((c, i) => (
                <input
                  key={i}
                  ref={(el) => (codeInputs.current[i] = el)}
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={c}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKey(i, e)}
                  className={`aya-auth-otp-cell ${c ? "is-filled" : ""}`}
                  data-testid={`auth-otp-${i}`}
                />
              ))}
            </div>

            {devCode && (
              <button
                type="button"
                className="aya-auth-devcode"
                onClick={autofillDevCode}
                data-testid="auth-dev-code"
                title="Cliquer pour remplir automatiquement le code"
              >
                💡 Code DEV (SMS mocké) : <strong>{devCode}</strong> · toucher pour remplir
              </button>
            )}

            <div className="aya-auth-resend">
              {resendIn > 0 ? (
                <span className="aya-auth-resend-wait" data-testid="auth-resend-wait">
                  <Clock3 size={15} /> Renvoyer le code dans <span className="aya-auth-green" style={{ fontWeight: 800 }}>{resendIn}s</span>
                </span>
              ) : (
                <button type="button" className="aya-auth-resend-btn" disabled={busy} onClick={requestOtp} data-testid="auth-resend-btn">
                  <Clock3 size={15} /> Renvoyer le code
                </button>
              )}
            </div>

            <button
              type="button"
              className="aya-auth-primary"
              disabled={busy || codeEntered < 6}
              onClick={verifyOtp}
              data-testid="auth-verify-otp-btn"
            >
              <span>{busy ? "Vérification…" : "Valider"}</span>
              <ArrowRight size={20} strokeWidth={2.6} className="aya-auth-primary-arrow" />
            </button>

            <div className="aya-auth-sep">
              <span className="aya-auth-sep-line" />
              <span className="aya-auth-sep-text">Numéro incorrect ?</span>
              <span className="aya-auth-sep-line" />
            </div>
            <button type="button" className="aya-auth-gold-btn" onClick={() => setStep(1)} data-testid="auth-change-phone">
              <Pencil size={15} />
              <span>Changer de numéro</span>
            </button>

            <button type="button" className="aya-auth-help" onClick={openSupport} data-testid="auth-help-card">
              <span className="aya-auth-help-icon"><Headphones size={20} color="#2EE27B" /></span>
              <span className="aya-auth-help-txt">
                <span className="aya-auth-help-title">Besoin d'aide ?</span>
                <span className="aya-auth-help-sub">Notre équipe est disponible 24h/24.</span>
              </span>
              <ChevronRight size={18} color="#9DB4A6" />
            </button>
          </>
        )}

        {/* ═══════════ ÉCRAN 3 — PROFIL ═══════════ */}
        {step === 3 && (
          <>
            <AuthHead />
            <Progress current={3} />
            <h2 className="aya-auth-title" style={{ marginTop: 4 }}>Complète ton <span className="aya-auth-green">profil</span></h2>
            <p className="aya-auth-sub">AYA personnalise tes salutations avec ton prénom.</p>

            <label className="aya-auth-label">Prénom ou nom d'affichage <span style={{ color: "#F97316" }}>*</span></label>
            <input
              type="text"
              maxLength={40}
              placeholder="Kouakou Jean"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && displayName.trim()) saveProfile(); }}
              className="aya-auth-input"
              autoFocus
              data-testid="auth-required-name"
            />
            <label className="aya-auth-label">Commune (optionnel)</label>
            <select
              value={commune}
              onChange={(e) => setCommune(e.target.value)}
              className="aya-auth-input"
              data-testid="auth-required-commune"
            >
              <option value="">— Choisir —</option>
              {COMMUNES_CIV.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              type="button"
              className="aya-auth-primary"
              disabled={busy || !displayName.trim()}
              onClick={saveProfile}
              data-testid="auth-save-profile-btn"
            >
              <span>{busy ? "Enregistrement…" : "C'est parti !"}</span>
              <ArrowRight size={20} strokeWidth={2.6} className="aya-auth-primary-arrow" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
